import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import { Approve } from "components/Prompts/Approve";
import { useActionHook, useModals, usePrompt } from "hooks";
import { usePortfolioConstructionActions } from "store";
import {
  ActiveSliceType,
  PortfolioPermissions,
  SliceGroup,
  SliceInvestor,
  SlicePromptResult,
  SliceSidebarTab
} from "store/types";

import { CreateSlicePrompt } from "../Create-slice-prompt/CreateSlicePrompt";

interface UseSlicesParams {
  investors: SliceInvestor[];
  permissions: PortfolioPermissions;
}

// Owns the slice domain: sidebar selection (groups vs investors), the loaded
// groups list, the derived "active slice", and the create/edit/duplicate/archive
// prompt flows. Groups load once on mount.
export const useSlices = ({ investors, permissions }: UseSlicesParams) => {
  const openPrompt = usePrompt();
  const { investorModal } = useModals();
  const { getSlicesAction, createSliceAction, updateSliceAction, duplicateSliceAction, deleteSliceAction } =
    usePortfolioConstructionActions();

  const { canCreateSlice, canDuplicateSlice, canEditSlice, canArchiveSlice } = permissions;

  // Selection state
  const [sliceTab, setSliceTab] = useState<SliceSidebarTab>("groups");
  const [selectedGroupId, setSelectedGroupIdState] = useState<SliceGroup["id"] | null>(null);
  const [selectedInvestorId, setSelectedInvestorIdState] = useState<SliceInvestor["id"] | null>(null);
  const [activeSliceKind, setActiveSliceKind] = useState<ActiveSliceType | null>(null);

  const setSelectedGroupId = useCallback((id: SliceGroup["id"] | null) => {
    setSelectedGroupIdState(id);
    if (id !== null) setSelectedInvestorIdState(null);
    setActiveSliceKind(id !== null ? "group" : null);
  }, []);

  const setSelectedInvestorId = useCallback((id: SliceInvestor["id"] | null) => {
    setSelectedInvestorIdState(id);
    if (id !== null) setSelectedGroupIdState(null);
    setActiveSliceKind(id !== null ? "investor" : null);
  }, []);

  const { data: groupsData, action: getSlices, setData: setGroupsData } = useActionHook(getSlicesAction);
  const groups = useMemo<SliceGroup[]>(() => groupsData ?? [], [groupsData]);

  // === Slice CRUD prompts (server handles persistence) ===

  const onAddGroup = useCallback(async () => {
    if (!canCreateSlice) return;
    let createdSlice: SliceGroup | null = null;
    const result = await openPrompt<{
      investors: SliceInvestor[];
      saveSlice: (data: SlicePromptResult) => Promise<void>;
      onSubmit: (result: SlicePromptResult) => void;
      onCancel: () => void;
    }>({
      Component: CreateSlicePrompt,
      componentType: "modal",
      disableBackdropClose: true,
      props: {
        investors,
        saveSlice: async (data) => {
          createdSlice = await createSliceAction({
            name: data.name,
            members: data.memberIds.map((id) => Number(id))
          });
        }
      }
    });
    if (!result || !createdSlice) return;
    const newSlice = createdSlice as SliceGroup;
    setGroupsData((prev) => [...(prev ?? []), newSlice]);
    setSelectedGroupId(newSlice.id);
    toast.success("Slice created.");
  }, [canCreateSlice, createSliceAction, investors, openPrompt, setGroupsData, setSelectedGroupId]);

  const onAddInvestor = useCallback(() => {
    investorModal({
      isOpen: true,
      getInvestor: (created) => {
        setSliceTab("investors");
        setSelectedInvestorId(created.id);
        toast.success("Investor created.");
      }
    });
  }, [investorModal, setSelectedInvestorId]);

  const onEditSlice = useCallback(
    async (group: SliceGroup) => {
      if (!canEditSlice(group)) return;
      let updatedSlice: SliceGroup | null = null;
      const result = await openPrompt<{
        mode?: "create" | "edit";
        initialSlice?: { name: string; memberIds: SliceInvestor["id"][] };
        investors: SliceInvestor[];
        saveSlice: (data: SlicePromptResult) => Promise<void>;
        onSubmit: (result: SlicePromptResult) => void;
        onCancel: () => void;
      }>({
        Component: CreateSlicePrompt,
        componentType: "modal",
        disableBackdropClose: true,
        props: {
          mode: "edit",
          initialSlice: { name: group.name, memberIds: group.members.map((m) => m.id) },
          investors,
          saveSlice: async (data) => {
            updatedSlice = await updateSliceAction(group.id, {
              name: data.name,
              members: data.memberIds.map((id) => Number(id))
            });
          }
        }
      });
      if (!result || !updatedSlice) return;
      const next = updatedSlice as SliceGroup;
      setGroupsData((prev) => (prev ?? []).map((g) => (g.id === group.id ? next : g)));
      toast.success("Slice updated.");
    },
    [canEditSlice, investors, openPrompt, setGroupsData, updateSliceAction]
  );

  const onDuplicateSlice = useCallback(
    async (group: SliceGroup) => {
      if (!canDuplicateSlice) return;
      try {
        const copy = await duplicateSliceAction(group.id);
        setGroupsData((prev) => [...(prev ?? []), copy]);
        setSelectedGroupId(copy.id);
        toast.success("Slice duplicated.");
      } catch {
        toast.error("Slice could not be duplicated.");
      }
    },
    [canDuplicateSlice, duplicateSliceAction, setGroupsData, setSelectedGroupId]
  );

  const onDeleteSlice = useCallback(
    async (group: SliceGroup) => {
      if (!canArchiveSlice(group)) return;
      const confirmed = await openPrompt<{
        title: string;
        description: string;
        warning?: string;
        confirmLabel?: string;
        onSubmit: (confirmed: boolean) => void;
        onCancel: () => void;
      }>({
        Component: Approve,
        componentType: "modal",
        disableBackdropClose: true,
        props: {
          title: `Archive ‘${group.name}’?`,
          description: "This slice will no longer appear in active investor segment views.",
          confirmLabel: "Archive slice"
        }
      });
      if (!confirmed) return;
      try {
        await deleteSliceAction(group.id);
        setGroupsData((prev) => (prev ?? []).filter((g) => g.id !== group.id));
        if (selectedGroupId === group.id) setSelectedGroupId(null);
        toast.success("Slice deleted.");
      } catch {
        toast.error("Slice could not be deleted.");
      }
    },
    [canArchiveSlice, deleteSliceAction, openPrompt, selectedGroupId, setGroupsData, setSelectedGroupId]
  );

  // === Active slice computed ===

  const { activeSliceKey, activeSliceType, activeSliceName } = useMemo(() => {
    if (activeSliceKind === "group" && selectedGroupId !== null) {
      const group = groups.find((g) => g.id === selectedGroupId);
      return {
        activeSliceKey: `group:${selectedGroupId}`,
        activeSliceType: "group" as ActiveSliceType,
        activeSliceName: group?.name ?? ""
      };
    }
    if (activeSliceKind === "investor" && selectedInvestorId !== null) {
      const inv = investors.find((i) => i.id === selectedInvestorId);
      return {
        activeSliceKey: `investor:${selectedInvestorId}`,
        activeSliceType: "investor" as ActiveSliceType,
        activeSliceName: inv?.name ?? ""
      };
    }
    return { activeSliceKey: null, activeSliceType: null, activeSliceName: "" };
  }, [activeSliceKind, selectedGroupId, selectedInvestorId, groups, investors]);

  useEffect(() => {
    getSlices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    sliceTab,
    setSliceTab,
    selectedGroupId,
    setSelectedGroupId,
    selectedInvestorId,
    setSelectedInvestorId,
    groups,
    activeSliceKey,
    activeSliceType,
    activeSliceName,
    onAddGroup,
    onAddInvestor,
    onEditSlice,
    onDuplicateSlice,
    onDeleteSlice
  };
};
